import { template as template_cee0e4feaac9410eae1b909016d523ef } from "@ember/template-compiler";
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { sortByProp, sortByProps } from 'ramda-adjunct';
import { t } from 'ember-intl';
import PowerSelect from 'eflex/components/power-select';
import { optional } from '@eflexsystems/ember-composable-helpers';
import { serviceHelper } from 'eflex/helpers';
import { fn } from '@ember/helper';
import { handleContextChange } from 'eflex/services/task-repo';
import PowerSelectMultiple from 'eflex/components/bs-form/element/control/power-select-multiple';
import { defaultMatcher } from 'ember-power-select/utils/group-utils';
const getFilteredModels = (treeItem, allowAll, isMultiSelect, defaultTaskConfigContext)=>{
    const options = sortByProp('code', treeItem.area.models ?? []);
    if (isMultiSelect) {
        options.unshift(defaultTaskConfigContext.getContext(treeItem, {
            selectFiltered: true
        }));
    }
    if (allowAll) {
        options.unshift(defaultTaskConfigContext.getContext(treeItem, {
            allSelected: true
        }));
    }
    return options;
};
const getSelectedMultiple = (selectedContext)=>{
    if (!Array.isArray(selectedContext)) {
        return [
            selectedContext
        ];
    }
    return selectedContext;
};
const getSelectedSingle = (selectedContext, filteredModels)=>{
    if (selectedContext.allSelected) {
        return filteredModels[0];
    } else {
        return selectedContext;
    }
};
const matcher = (option, searchTerm)=>{
    return option.allSelected || option.selectFiltered || defaultMatcher(option.displayName, searchTerm);
};
export const ModelContextMultiSelector = template_cee0e4feaac9410eae1b909016d523ef(`
<div class="row form-row model-context-selector" ...attributes>
  <div class="col">
    <label class="form-label">
      {{t "model"}}
    </label>
    {{#let
      (getFilteredModels @treeItem @allowAll @multiSelect (serviceHelper 'defaultTaskConfigContext'))
    as |filteredModels|}}
      <PowerSelectMultiple
        class="plant-model-select options-select sm"
        @options={{filteredModels}}
        @value={{getSelectedMultiple @selectedContext}}
        @onChange={{optional @contextWasSelected}}
        @searchEnabled={{true}}
        @searchField="displayName"
        @renderInPlace={{@renderInPlace}}
        @matcher={{matcher}}
      as |model|>
        {{model.displayName}}
      </PowerSelectMultiple>
    {{/let}}
  </div>
</div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export const ModelContextSelector = template_cee0e4feaac9410eae1b909016d523ef(`
<div class="row form-row model-context-selector" ...attributes>
  <div class="col">
    <label class="form-label">
      {{t "model"}}
    </label>
    {{#let
      (getFilteredModels @treeItem @allowAll @multiSelect (serviceHelper 'defaultTaskConfigContext'))
    as |filteredModels|}}
      <PowerSelect
        class="plant-model-select options-select sm"
        @options={{filteredModels}}
        @selected={{getSelectedSingle @selectedContext filteredModels}}
        @searchField="displayName"
        @renderInPlace={{@renderInPlace}}
        @onChange={{optional @contextWasSelected}}
      as |model|>
        {{model.displayName}}
      </PowerSelect>
    {{/let}}
  </div>
</div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const getSortedAreaComponents = (components)=>{
    return sortByProps([
        'alwaysRunSort',
        'isComplex',
        'priority'
    ], components ?? []);
};
const getComponentOptions = (treeItem, allowAll, isMultiSelect, defaultTaskConfigContext)=>{
    let options = [];
    if (isMultiSelect) {
        options.unshift(defaultTaskConfigContext.getContext(treeItem, {
            selectFiltered: true
        }));
    }
    if (allowAll) {
        options.unshift(defaultTaskConfigContext.getContext(treeItem, {
            allSelected: true
        }));
    }
    const component = treeItem.component;
    if (component) {
        options = options.concat(sortByProps([
            'alwaysRunSort',
            'isComplex',
            'priority'
        ], component.options));
    }
    return options;
};
export class ComponentContextSelector extends Component {
    @service
    taskRepo;
    @service
    defaultTaskConfigContext;
    onSelectedComponent = (treeItem, componentOptions, component)=>{
        if (component == null || component === treeItem.component) {
            return;
        }
        treeItem.taskConfigs.forEach((taskConfig)=>{
            taskConfig.deleteRecord();
        });
        this.taskRepo.createComponentOptionConfigs(treeItem, component);
        handleContextChange(treeItem, component);
        treeItem.component = component;
        this.args.contextWasSelected(componentOptions[0]);
    };
    static{
        template_cee0e4feaac9410eae1b909016d523ef(`
    <div class="component-context-selector row gx-2" ...attributes>
      {{#let
        (getComponentOptions @treeItem @allowAll @multiSelect this.defaultTaskConfigContext)
      as |componentOptions|}}
        <div class="col-6">
          <div class="component-select-wrapper">
            <label class="form-label">
              {{t "components"}}
            </label>
            <PowerSelect
              class="app-component-select sm"
              @options={{getSortedAreaComponents @treeItem.area.bomSource.components}}
              @selected={{@treeItem.component}}
              @searchEnabled={{true}}
              @searchField="displayName"
              @renderInPlace={{@renderInPlace}}
              @onChange={{fn this.onSelectedComponent @treeItem componentOptions}}
              @disabled={{@disableComponentSelect}}
            as |comp|>
              {{comp.displayName}}
            </PowerSelect>
          </div>
        </div>
        {{#unless @treeItem.component.isAlwaysRun}}
          <div class="col-6">
            <label class="form-label">
              {{t "options"}}
            </label>
            <PowerSelectMultiple
              class="app-option-select options-select sm"
              @options={{componentOptions}}
              @value={{getSelectedMultiple @selectedContext}}
              @searchEnabled={{true}}
              @searchField="name"
              @renderInPlace={{@renderInPlace}}
              @placeholder={{t "options"}}
              @onChange={{optional @contextWasSelected}}
              @matcher={{matcher}}
            as |opt|>
              {{#if opt.value}}
                {{opt.displayName}} - {{opt.value}}
              {{else}}
                {{opt.displayName}}
              {{/if}}
            </PowerSelectMultiple>
          </div>
        {{/unless}}
      {{/let}}
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default template_cee0e4feaac9410eae1b909016d523ef(`
{{#if @treeItem.usesComponents}}
  <ComponentContextSelector
    @treeItem={{@treeItem}}
    @renderInPlace={{@renderInPlace}}
    @selectedContext={{@selectedContext}}
    @contextWasSelected={{@contextWasSelected}}
    @allowAll={{@allowAll}}
    @disableComponentSelect={{@disableComponentSelect}}
    @multiSelect={{@multiSelect}}
    ...attributes
  />
{{else}}
  {{#if @multiSelect}}
    <ModelContextMultiSelector
      @treeItem={{@treeItem}}
      @renderInPlace={{@renderInPlace}}
      @selectedContext={{@selectedContext}}
      @contextWasSelected={{@contextWasSelected}}
      @allowAll={{@allowAll}}
      @multiSelect={{@multiSelect}}
      ...attributes
    />
  {{else}}
    <ModelContextSelector
      @treeItem={{@treeItem}}
      @renderInPlace={{@renderInPlace}}
      @selectedContext={{@selectedContext}}
      @contextWasSelected={{@contextWasSelected}}
      @allowAll={{@allowAll}}
      @multiSelect={{@multiSelect}}
      ...attributes
    />
  {{/if}}
{{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
